import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CORE_STATUSES } from '../types/bsc';
import { STATUS_COLORS } from '../config/kpi';
import type { KpiStatus, SnapshotRow } from '../types/bsc';
import { statusCounts } from '../data/model';

interface StatusChartProps {
  snapshot: SnapshotRow[];
}

const FALLBACK_STATUS_ORDER: KpiStatus[] = [
  ...CORE_STATUSES,
  'Sotto target',
  'Manca dato',
  'Da definire',
  'No target',
];

export function StatusChart({ snapshot }: StatusChartProps) {
  const counts = statusCounts(snapshot);
  const data = FALLBACK_STATUS_ORDER.filter(
    (status) => CORE_STATUSES.includes(status as (typeof CORE_STATUSES)[number]) || counts[status] > 0,
  ).map((status) => ({
    status,
    count: counts[status] ?? 0,
    fill: STATUS_COLORS[status],
  }));

  return (
    <section className="panel status-panel" aria-labelledby="status-chart-title">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">Sintesi</span>
          <h2 id="status-chart-title">Stato KPI</h2>
        </div>
        <span className="panel-note">{snapshot.length} KPI visualizzati</span>
      </div>

      <div className="status-chart" role="img" aria-label="Grafico a barre del numero di KPI per stato">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 24, right: 20, bottom: 8, left: 0 }}
            barCategoryGap="24%"
          >
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="status" tickLine={false} axisLine={false} interval={0} />
            <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={30} />
            <Tooltip
              cursor={{ fill: 'rgba(15, 23, 42, 0.04)' }}
              formatter={(value: unknown) => [Number(value), 'KPI']}
            />
            <Bar dataKey="count" name="KPI" radius={[6, 6, 0, 0]} minPointSize={2}>
              {data.map((entry) => (
                <Cell key={entry.status} fill={entry.fill} />
              ))}
              <LabelList dataKey="count" position="insideTop" fill="#ffffff" fontSize={18} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="status-legend" aria-label="Legenda stati">
        {data.map((entry) => (
          <span key={entry.status} className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: entry.fill }} />
            {entry.status}: <strong>{entry.count}</strong>
          </span>
        ))}
      </div>
    </section>
  );
}
